import { BareActionContext, getStoreBuilder } from "vuex-typex";
import { RootState } from "./RootState";
import { ProductModel, Variant, VariantFilterOptionsModel } from "../models/product";
import preloadState from "../services/PreloadState";

export interface ProductState {
    product: ProductModel;
    variants: Variant[];
    variant: Variant;
    variantFilterOptions: VariantFilterOptionsModel[];
    quantity: number;
    palletRequired: number;
}

const initialState: ProductState = {
    product: preloadState.product,
    variants: preloadState.product.variants,
    variant: getInitialVariant(preloadState.product),
    variantFilterOptions: preloadState.product.variantFilterOptions,
    quantity: 0,
    palletRequired: 0
};

const store = getStoreBuilder<RootState>().module("product", initialState);

function getInitialVariant(curProduct: ProductModel): Variant {
    if (curProduct.selectedVariantId) {
        const curVariant = curProduct.variants.find(x => x.variantId === curProduct.selectedVariantId);

        if (curVariant) {
            return curVariant;
        }
    }
    if (preloadState.product.variants.length) {
        return preloadState.product.variants[0];
    }

    return {} as Variant;
}

function variantFilterOptions(state: ProductState) {
    return state.variantFilterOptions;
}

function variants(state: ProductState) {
    return state.variants;
}

function variant(state: ProductState) {
    return state.variant;
}

function product(state: ProductState) {
    return state.product;
}

function quantity(state: ProductState) {
    return state.quantity;
}

function palletRequired(state: ProductState) {
    return state.palletRequired;
}


// getters
const getVariantFilterOptions = store.read(variantFilterOptions, "variantFilterOptions");

const getVariants = store.read(variants, "variants");

const getVariant = store.read(variant, "variant");

const getProduct = store.read(product, "product");

const getQuantity = store.read(quantity, "quantity");

const getPalletRequired = store.read(palletRequired, "palletRequired");

// mutations
function commitVariant(state: ProductState, payload: Variant) {
    state.variant = payload;
}

function commitQuantity(state: ProductState, payload: number) {
    state.quantity = payload;
}

function commitPalletRequired(state: ProductState, payload: number) {
    state.palletRequired = payload;
}

// actions
async function dispatchVariant(context: BareActionContext<ProductState, RootState>, curVariant: Variant) {
    productStore.commitVariant(curVariant);
    setUrl(curVariant);
}

async function dispatchQuantity(context: BareActionContext<ProductState, RootState>, value: number) {
    commitQuantity(context.state, value);
}

async function dispatchPalletRequired(context: BareActionContext<ProductState, RootState>, value: number) {
    commitPalletRequired(context.state, value);
}

async function setUrl(currentVariant: Variant) {
    const url = new URL(window.location.href);
    url.searchParams.set("id", currentVariant.variantId);
    window.history.replaceState(null, currentVariant.variantName, url.search);
}

export const productStore = {
    get variantFilterOptions() { return getVariantFilterOptions(); },
    get variants() { return getVariants(); },
    get variant() { return getVariant(); },
    get product() { return getProduct(); },
    get quantity() { return getQuantity(); },
    get palletRequired() { return getPalletRequired(); },
    commitVariant: store.commit(commitVariant, "commitVariant"),
    dispatchVariant: store.dispatch(dispatchVariant, "dispatchVariant"),
    dispatchQuantity: store.dispatch(dispatchQuantity, "dispatchQuantity"),
    dispatchPalletRequired: store.dispatch(dispatchPalletRequired, "dispatchPalletRequired")
};
